/**
 * ResumeWorkPanel — UNIFIED_WORK_LIFECYCLE_RESUME_PANEL_V1
 *
 * Shows the current work item from persisted context with available next steps.
 * Renders only when saved context exists.
 *
 * BOUNDARY:
 * - resume panel = navigation aid
 * - resume panel ≠ workflow execution
 * - resume panel ≠ approval
 * - buttons open existing pages, never trigger state changes
 */

import React, { useState } from 'react';
import {
  FileText,
  Boxes,
  LayoutDashboard,
  Receipt,
  Network,
  AlertTriangle,
  Clock,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { useWorkLifecycleContext } from './useWorkLifecycleContext';

interface NextStep {
  id: string;
  label: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  available: boolean;
  missingReason?: string;
}

function buildNextSteps(context: ReturnType<typeof useWorkLifecycleContext>['context']): NextStep[] {
  return [
    {
      id: 'request',
      label: 'Open Request',
      description: 'View governed request, status, and audit trail',
      href: context.requestId ? `/requests/${context.requestId}` : '/requests',
      icon: <FileText className="w-4 h-4" />,
      available: !!context.requestId,
      missingReason: context.requestId ? undefined : 'No requestId in context',
    },
    {
      id: 'gwu',
      label: 'Open Work Unit (GWU)',
      description: 'Inspect the governed work unit, its state, and receipts',
      href: context.gwuId
        ? `/formation-gate/gwu-registry?gwuId=${context.gwuId}`
        : '/formation-gate/gwu-registry',
      icon: <Boxes className="w-4 h-4" />,
      available: !!context.gwuId,
      missingReason: context.gwuId ? undefined : 'No gwuId in context — create or link a GWU first',
    },
    {
      id: 'workspace',
      label: 'Open Workspace Inspection',
      description: 'Inspect governance events, continuity, and evidence for this work item',
      href: context.gwuId
        ? `/governance-workspace?gwuId=${context.gwuId}${context.requestId ? `&requestId=${context.requestId}` : ''}`
        : `/governance-workspace${context.requestId ? `?requestId=${context.requestId}` : ''}`,
      icon: <LayoutDashboard className="w-4 h-4" />,
      available: true,
    },
    {
      id: 'receipts',
      label: 'Open Receipts',
      description: 'View proof receipts and verification status',
      href: context.receiptId
        ? `/verify/${context.receiptId}`
        : context.requestId
        ? `/requests/${context.requestId}`
        : '/verify',
      icon: <Receipt className="w-4 h-4" />,
      available: !!(context.receiptId || context.requestId),
      missingReason: (!context.receiptId && !context.requestId)
        ? 'No receiptId or requestId in context'
        : undefined,
    },
    {
      id: 'relationships',
      label: 'Open Relationships',
      description: 'View relationship network for this work item',
      href: context.gwuId
        ? `/formation-gate/relationship-observability?gwuId=${context.gwuId}`
        : '/formation-gate/relationship-observability',
      icon: <Network className="w-4 h-4" />,
      available: !!context.gwuId,
      missingReason: context.gwuId ? undefined : 'No gwuId in context — link a GWU to inspect relationships',
    },
  ];
}

interface ResumeWorkPanelProps {
  className?: string;
}

export const ResumeWorkPanel: React.FC<ResumeWorkPanelProps> = ({ className = '' }) => {
  const { context, health, clear } = useWorkLifecycleContext();
  const [confirmClear, setConfirmClear] = useState(false);
  const [showMissing, setShowMissing] = useState(false);

  if (!health.hasContext) return null;

  const steps = buildNextSteps(context);
  const availableSteps = steps.filter(s => s.available);
  const unavailableSteps = steps.filter(s => !s.available);

  const savedAt = context.savedAt
    ? new Date(context.savedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className={`bg-slate-900 border border-slate-800 rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-4 border-b border-slate-800">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-white">Resume Work</h3>
            {health.isStale && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-900/40 border border-amber-700/40 rounded text-amber-400 text-xs">
                <AlertTriangle className="w-3 h-3" />
                stale
              </span>
            )}
          </div>

          {/* Work item summary */}
          <div className="flex items-center gap-2 flex-wrap">
            {context.label && (
              <span className="text-slate-300 text-sm">{context.label}</span>
            )}
            <div className="flex items-center gap-1.5 flex-wrap text-xs">
              {context.requestId && (
                <span className="inline-flex items-center px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-slate-400 font-mono">
                  req:{context.requestId.slice(0, 10)}…
                </span>
              )}
              {context.gwuId && (
                <>
                  {context.requestId && <ChevronRight className="w-3 h-3 text-slate-600" />}
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-slate-400 font-mono">
                    gwu:{context.gwuId.slice(0, 10)}…
                  </span>
                </>
              )}
              {context.receiptId && (
                <>
                  <ChevronRight className="w-3 h-3 text-slate-600" />
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-slate-400 font-mono">
                    rcpt:{context.receiptId.slice(0, 10)}…
                  </span>
                </>
              )}
              {context.relationshipId && (
                <>
                  <ChevronRight className="w-3 h-3 text-slate-600" />
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-slate-400 font-mono">
                    rel:{context.relationshipId.slice(0, 10)}…
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Metadata row */}
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
            {savedAt && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Saved {savedAt}
              </span>
            )}
            {health.isStale && (
              <span className="text-amber-500">
                Context may not reflect current governed state
              </span>
            )}
          </div>
        </div>

        {/* Clear action */}
        <div className="flex-shrink-0">
          {confirmClear ? (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-400">Clear context?</span>
              <button
                onClick={() => { clear(); setConfirmClear(false); }}
                className="px-2 py-1 bg-red-900/50 hover:bg-red-900 border border-red-700/50 text-red-300 rounded transition-colors"
              >
                Clear
              </button>
              <button
                onClick={() => setConfirmClear(false)}
                className="px-2 py-1 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmClear(true)}
              title="Clear navigation context"
              className="flex items-center gap-1.5 px-2 py-1 text-xs text-slate-500 hover:text-slate-300 hover:bg-slate-800 border border-transparent hover:border-slate-700 rounded transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Available next steps */}
      <div className="px-5 py-4">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">Available steps</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {availableSteps.map((step) => (
            <a
              key={step.id}
              href={step.href}
              className="flex items-start gap-3 p-3 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 hover:border-slate-600 rounded-lg transition-all group"
            >
              <div className="mt-0.5 text-slate-400 group-hover:text-slate-200 transition-colors flex-shrink-0">
                {step.icon}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-200 group-hover:text-white transition-colors">
                  {step.label}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 leading-snug">
                  {step.description}
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Missing context — collapsible */}
        {unavailableSteps.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowMissing(v => !v)}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-400 transition-colors"
            >
              <AlertTriangle className="w-3 h-3 text-amber-600" />
              {unavailableSteps.length} step{unavailableSteps.length > 1 ? 's' : ''} need additional context
              <ChevronRight className={`w-3 h-3 transition-transform ${showMissing ? 'rotate-90' : ''}`} />
            </button>

            {showMissing && (
              <div className="mt-2 space-y-1.5">
                {unavailableSteps.map((step) => (
                  <div
                    key={step.id}
                    className="flex items-start gap-3 p-2.5 bg-slate-800/30 border border-slate-800 rounded-lg opacity-60"
                  >
                    <div className="mt-0.5 text-slate-600 flex-shrink-0">
                      {step.icon}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-slate-500">
                        {step.label}
                      </div>
                      {step.missingReason && (
                        <div className="text-xs text-slate-600 mt-0.5">
                          {step.missingReason}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Boundary notice */}
      <div className="px-5 pb-4">
        <p className="text-xs text-slate-600 border-t border-slate-800 pt-3">
          Navigation memory only — opening any item does not approve or execute governed operations.
        </p>
      </div>
    </div>
  );
};
